import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../assets/css/AssessmentPage/AssessmentPage.css";
import { BASE_URL } from "../../store/config";
import axios from "axios";
import ErrorModal from "../common/ErrorModal";

function AssessmentPage() {

  const { id } = useParams();
  const navigate = useNavigate();

  //---------------------------------------------------//
  //-------------------- STATE ------------------------//
  //---------------------------------------------------//

  const [attemptId, setAttemptId] = useState(null);

  const attemptCreated = useRef(false);

  const [attemptStarted, setAttemptStarted] = useState(false);

  const [currentQ, setCurrentQ] = useState(0);

  const [answers, setAnswers] = useState({});

  const [visited, setVisited] = useState({});

  const [questions, setQuestions] = useState([]);

  const [assessmentTitle, setAssessmentTitle] = useState("");

  const [totalTime, setTotalTime] = useState(0);

  const [timeLeft, setTimeLeft] = useState(0);

  const [warningShown, setWarningShown] = useState(false);

  const [showWarning, setShowWarning] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);

  const [loadingAttempt, setLoadingAttempt] = useState(true);

  const [error, setError] = useState({
    show: false,
    message: "",
  });

  //---------------------------------------------------//
  //-------------------- TOKEN ------------------------//
  //---------------------------------------------------//

  const token =
    localStorage.getItem("access_token") ||
    localStorage.getItem("accessToken");

  //---------------------------------------------------//
  //-------------------- ERROR ------------------------//
  //---------------------------------------------------//

  const showError = (msg) => {
    setError({
      show: true,
      message: msg,
    });
  };

  const closeError = () => {
    setError({
      show: false,
      message: "",
    });
  };

  //---------------------------------------------------//
  //-------------------- TIMER ------------------------//
  //---------------------------------------------------//

  useEffect(() => {

    if (totalTime === 0) return;

    const timer = setInterval(() => {

      setTimeLeft((prev) => {

        if (prev <= 1) {

          clearInterval(timer);

          handleSubmit(true);

          return 0;
        }

        if (
          prev <= Math.floor(totalTime * 0.2) &&
          !warningShown
        ) {
          setShowWarning(true);
          setWarningShown(true);
        }

        return prev - 1;
      });

    }, 1000);

    return () => clearInterval(timer);

  }, [warningShown, totalTime]);

  //---------------------------------------------------//
  //---------------- FETCH ASSESSMENT -----------------//
  //---------------------------------------------------//

  useEffect(() => {

    const fetchAssessment = async () => {

      try {

        const token =
          localStorage.getItem("access_token") ||
          localStorage.getItem("accessToken");

        if (!token) {

          showError("Please login first");

          navigate("/login");

          return;
        }

        const res = await axios.get(
          `${BASE_URL}api/assessments/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = res.data;

        console.log("ASSESSMENT DATA:", data);

        setAssessmentTitle(data.title);

        const duration = Number(data.duration || 0) * 60;

        setTotalTime(duration);

        setTimeLeft(duration);

        const formatted = (data.questions || []).map((q) => ({

          id: q.id,

          question:
            q.question_data?.question_text ||
            q.title,

          options:
            q.question_data?.options || [],

          marks:
            q.question_data?.marks ||
            q.marks ||
            1,

        }));

        setQuestions(formatted);

      } catch (err) {

        console.error(
          "Assessment fetch error:",
          err
        );

        showError("Failed to load assessment");
      }
    };

    fetchAssessment();

  }, [id]);

  //---------------------------------------------------//
  //---------------- START ATTEMPT --------------------//
  //---------------------------------------------------//



  useEffect(() => {

    //---------------------------------------------------
    // PREVENT DOUBLE API CALL
    //---------------------------------------------------

    if (attemptCreated.current) return;

    attemptCreated.current = true;

    const startAttempt = async () => {

      try {

        setLoadingAttempt(true);

        const token =
          localStorage.getItem("access_token") ||
          localStorage.getItem("accessToken");

        if (!token) {

          showError("Please login first");

          navigate("/login");

          return;
        }

        //---------------------------------------------------
        // START ATTEMPT API
        //---------------------------------------------------

        const res = await axios.post(
          `${BASE_URL}api/attempts?assessment_id=${id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log(
          "✅ START ATTEMPT RESPONSE:",
          res.data
        );

        //---------------------------------------------------
        // STORE ATTEMPT ID
        //---------------------------------------------------

        setAttemptId(res.data.attempt_id);

        console.log(
          "✅ ATTEMPT ID STORED:",
          res.data.attempt_id
        );

      } catch (err) {

        console.error(
          "❌ Start attempt error:",
          err
        );

        showError(
          err.response?.data?.detail ||
          "Failed to start attempt"
        );

      } finally {

        setLoadingAttempt(false);
      }
    };

    startAttempt();

  }, [id]);

  //---------------------------------------------------//
  //------------------ FULLSCREEN ---------------------//
  //---------------------------------------------------//

  useEffect(() => {

    const enterFullscreen = async () => {

      try {

        if (!document.fullscreenElement) {

          await document.documentElement.requestFullscreen();
        }

      } catch (err) {

        console.log(
          "Fullscreen error:",
          err
        );
      }
    };

    enterFullscreen();

    const handleExit = () => {

      if (!document.fullscreenElement) {

        showError(
          "You cannot exit fullscreen during the test!"
        );

        enterFullscreen();
      }
    };

    document.addEventListener(
      "fullscreenchange",
      handleExit
    );

    return () => {

      document.removeEventListener(
        "fullscreenchange",
        handleExit
      );
    };

  }, []);

  //---------------------------------------------------//
  //------------------ TIME WARNING -------------------//
  //---------------------------------------------------//

  useEffect(() => {

    if (showWarning) {

      const timer = setTimeout(() => {

        setShowWarning(false);

      }, 5000);

      return () => clearTimeout(timer);
    }

  }, [showWarning]);

  //---------------------------------------------------//
  //------------------ VISITED TRACK ------------------//
  //---------------------------------------------------//

  useEffect(() => {

    if (!questions.length) return;

    setVisited((prev) => ({
      ...prev,
      [questions[currentQ]?.id]: true,
    }));

  }, [currentQ, questions]);

  //---------------------------------------------------//
  //------------------ HANDLERS -----------------------//
  //---------------------------------------------------//

  const handleOptionSelect = (qid, value) => {

    setAnswers((prev) => ({
      ...prev,
      [qid]: value,
    }));
  };

  const handleNext = () => {

    if (currentQ < questions.length - 1) {

      setCurrentQ(currentQ + 1);
    }
  };

  const handlePrev = () => {

    if (currentQ > 0) {

      setCurrentQ(currentQ - 1);
    }
  };

  //---------------------------------------------------//
  //------------------ SUBMIT TEST --------------------//
  //---------------------------------------------------//

  const handleSubmit = async (autoSubmit = false) => {

    try {

      //---------------------------------------------------
      // TOKEN
      //---------------------------------------------------

      const token =
        localStorage.getItem("access_token") ||
        localStorage.getItem("accessToken");

      if (!token) {
        showError("Please login first");
        navigate("/login");
        return;
      }

      //---------------------------------------------------
      // ATTEMPT CHECK
      //---------------------------------------------------

      if (!attemptId) {
        showError("Assessment attempt not created yet");
        return;
      }

      //---------------------------------------------------
      // EMPTY ANSWERS CHECK
      //---------------------------------------------------

      if (!autoSubmit && Object.keys(answers).length === 0) {
        showError("Please answer at least one question");
        return;
      }

      //---------------------------------------------------
      // PAYLOAD
      //---------------------------------------------------

      const payload = {
        attempt_id: Number(attemptId),
        responses: answers,
      };

      console.log("🚀 SUBMIT PAYLOAD:", payload);

      //---------------------------------------------------
      // API CALL
      //---------------------------------------------------

      const res = await axios.post(
        `${BASE_URL}api/attempts/submit`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("✅ SUBMITTED:", res.data);

      //---------------------------------------------------
      // SUCCESS
      //---------------------------------------------------

      navigate(`/submit/${attemptId}`);

    } catch (err) {

      console.error("❌ Submit error:", err);

      console.log("ERROR RESPONSE:", err.response);

      showError(
        err.response?.data?.detail ||
        "Submission failed"
      );
    }
  };

  //---------------------------------------------------//
  //------------------ HELPERS ------------------------//
  //---------------------------------------------------//

  const formatTime = (time) => {

    const mins = Math.floor(time / 60);

    const secs = time % 60;

    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const q = questions[currentQ];

  //---------------------------------------------------//
  //-------------------- UI ---------------------------//
  //---------------------------------------------------//

  return (

    <div className="assessment-container-page">

      {/* SIDEBAR */}
      <div className="sidebar">

        <h3>Questions</h3>

        <div className="palette">

          {questions.map((ques, index) => {

            let status = "not-visited";

            if (currentQ === index)
              status = "current";

            else if (answers[ques.id])
              status = "answered";

            else if (visited[ques.id])
              status = "visited";

            return (

              <button
                key={ques.id}
                className={`q-btn ${status}`}
                onClick={() =>
                  setCurrentQ(index)
                }
              >
                {index + 1}
              </button>
            );
          })}

        </div>

        <div className="legend">

          <h4 style={{ letterSpacing: "0.5px" }}>
            Question Status Guide
          </h4>

          <div className="legend-item">
            <span className="circle not-visited"></span>
            <p>Unvisited</p>
          </div>

          <div className="legend-item">
            <span className="circle visited"></span>
            <p>Visited</p>
          </div>

          <div className="legend-item">
            <span className="circle answered"></span>
            <p>Answered</p>
          </div>

          <div className="legend-item">
            <span className="circle current"></span>
            <p>Current</p>
          </div>

        </div>
      </div>

      {/* MAIN */}
      <div className="main">

        {/* HEADER */}
        <div className="header">

          <img
            src="/assets/images/image.png"
            alt="Logo"
            style={{ height: "50px" }}
          />

          <h2 style={{ color: "white" }}>{assessmentTitle}</h2>

          <h3>
            ⏱️ {formatTime(timeLeft)}
          </h3>

        </div>

        {/* QUESTION */}
        {q && (

          <>

            <div className="question-box">

              {/* MARKS BADGE */}
              <div className="question-marks">
                {q?.marks} marks
              </div>

              <h3>
                <strong>Q{currentQ + 1}.</strong>{" "}
                <span dangerouslySetInnerHTML={{ __html: q.question }} />
              </h3>

            </div>

            <div className="options">

              {(q.options || []).length > 0 ? (

                q.options.map((opt) => (

                  <label key={opt}>

                    <input
                      type="radio"
                      checked={
                        answers[q.id] === opt
                      }
                      onChange={() =>
                        handleOptionSelect(
                          q.id,
                          opt
                        )
                      }
                    />

                    {opt}
                  </label>
                ))

              ) : (

                <p>No options available</p>
              )}

            </div>

          </>
        )}

        {/* NAVIGATION */}
        <div className="nav-buttons">

          {currentQ > 0 && (

            <button onClick={handlePrev}>
              Previous
            </button>
          )}

          {currentQ < questions.length - 1 && (

            <button
              className="nav-buttons-right"
              onClick={handleNext}
            >
              Next
            </button>
          )}

        </div>

        {/* SUBMIT */}
        <button
          className="submit-btn"
          onClick={() =>
            handleSubmit(false)
          }
          disabled={loadingAttempt}
        >

          {loadingAttempt
            ? "Preparing Attempt..."
            : "Submit Assessment"}

        </button>

      </div>

      {/* ERROR MODAL */}
      <ErrorModal
        show={error.show}
        message={error.message}
        onClose={closeError}
      />

    </div>
  );
}

export default AssessmentPage;